/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  type Dispatch,
  useCallback,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
} from 'react';
import type { FieldValues, UseFormReturn } from 'react-hook-form';
import { useAsync } from 'react-streaming';

import type { WithFormErrors } from '@/components/Forms/FormControl';
// import { LoginForm } from '@/pages/(auth)/signin/LoginForm';
import { request, requestCacheKey } from '@/renderer/utils/api';
import type { RequestError } from '@/renderer/utils/errors';
import { ExpiredSessionError, NoSessionError } from '@/renderer/utils/errors';
import { useData } from '@/renderer/utils/useData';
import { usePageContext } from '@/renderer/utils/usePageContext';
import type { JSONValue } from '@/types/json';
import type { Method, RequestCache, RequestSchema } from '@/types/request';
import { useDebounce } from 'use-debounce';
import { useDeepMemo } from './hooks/useDeepMemo';
import { useEffectOnce } from './hooks/useEffectOnce';
import { useIsFirstRender } from './hooks/useIsFirstRender';
import { useRefUpdating } from './hooks/useRefUpdating';
import { useUpdateEffect } from './hooks/useUpdateEffect';
import { IS_SERVER } from './ssr';
import { forceLogoutEventTarget } from './useMe';

export default useRequest;

export interface UseRequestOpts<T, Q> {
  manual?: boolean;
  cache?: RequestCache;
  clientOnly?: boolean;
  id?: string;
  // @todo somehow cast this better
  // @TODO biome-ignore lint/suspicious/noExplicitAny:
  formatResult?: (data: any) => T;
  method?: Method;
  query?: Q;
  hideAlert?: boolean;
  skipRefresh?: boolean;
  storeRaw?: boolean;
}

interface UseRequestRunFnOpts<RE extends boolean, Raw extends boolean, PassedQuery = undefined> {
  throwOnRun?: boolean;
  returnError?: RE;
  rawResult?: Raw;
  dontStoreError?: boolean;
  dontAlterState?: boolean;
  query?: PassedQuery;
  skipAbort?: boolean;
  abortController?: AbortController;
  req?: Partial<RequestSchema<any, any>>;
  // @todo AbortController can be reinitialized in run(); we should be able to get the whole promise + new abort
  wrapped?: boolean;
}

type UseRequestRunFnResult<T, PassedRE extends boolean, PassedRaw extends boolean> = Promise<
  ExtractRaw<PassedRaw, T> | (PassedRE extends true ? Error | null : null)
>;

type UseRequestRunFn<T, D, _E = unknown | RequestError | Error> = <
  const PassedRE extends boolean = false,
  const PassedRaw extends boolean = false,
  const PassedQuery = undefined,
>(
  props?: D,
  opts?: UseRequestRunFnOpts<PassedRE, PassedRaw, PassedQuery>,
) => UseRequestRunFnResult<T, PassedRE, PassedRaw>;

type ExtractRaw<Raw extends boolean, T> = Raw extends true
  ? T
  : T extends { data: infer B }
    ? B
    : T;

function extractResult<const T, const R = T extends { data: infer B } ? B : T>(
  obj: T,
  raw: boolean | undefined,
) {
  if (raw) {
    return obj as T;
  }

  return ((obj as { data: any })?.data || obj) as R;
}

interface RequestReducerState<T> {
  data: T | undefined;
  error: Error | null | undefined;
  loading: boolean;
  pending: boolean;
}

function requestReducer<T>(
  state: RequestReducerState<T>,
  {
    action,
    values,
  }:
    | { action: 'RESET'; values?: Partial<RequestReducerState<T>> }
    | { action: 'SET'; values: Partial<RequestReducerState<T>> },
) {
  if (action === 'SET' && values) {
    let changed = 0;

    if (!state) {
      state = { data: undefined, error: undefined, loading: false, pending: false };
      changed = -1;
    }

    for (const k in values) {
      const key = k as keyof RequestReducerState<T>;
      if (state[key] !== values[key]) {
        state[key] = values[key];
        changed = changed === 0 ? 1 : changed;
      }
    }

    if (changed === 1) {
      return { ...state };
    }
  } else if (action === 'RESET') {
    // @todo
  }

  return state;
}

const USEREQUESTID = '#useRequest#';

export interface UseRequestResult<T, D, E = unknown | RequestError | Error> {
  data: T | undefined;
  error?: E;
  loading: boolean;
  pending: boolean;
  dispatch: Dispatch<
    | {
        action: 'RESET';
        values?: Partial<RequestReducerState<any>>;
      }
    | {
        action: 'SET';
        values: Partial<RequestReducerState<any>>;
      }
  >;
  run: UseRequestRunFn<T, D, E>;
  abort: AbortController | undefined;
  reset: () => void;
  // LoginForm?: typeof LoginForm;
}

export interface SubFormProps<V extends FieldValues> {
  form: UseFormReturn<WithFormErrors<V>>;
  onSubmit: (data?: V | any) => Promise<void> | void;
  request: UseRequestResult<any, V>;
}

/** Errors from the server need to be serialized into a plain object first */
function errorToObject(item: Error) {
  const obj: Record<any, any> = {};

  const descriptors = Object.getOwnPropertyDescriptors(item);

  for (const key in descriptors) {
    let apply = descriptors[key];
    const typed = typeof apply;

    if (typed !== 'function') {
      if (typed === 'object') {
        for (const key2 in apply) {
          if (typeof apply[key2 as keyof typeof apply] === 'function') {
            // To get rid of Error.stack.get. Could be done with less code but this catches other edge cases, if any.
            apply = {
              ...apply,
              [key2]: undefined,
            };
          }
        }
      }

      obj[key] = descriptors[key];
    }
  }

  obj.__constructor__ = item.constructor?.name || 'Error';

  return obj;
}

/** When loading cached errors on the client, deserialize them back */
function errorFromObject(item: Record<any, any>) {
  const { __constructor__, ...props } = item;
  const str = __constructor__;
  let proto = Error.prototype;

  if (typeof str === 'string' && /^[a-zA-Z0-9_]+$/.test(str)) {
    try {
      const constructorObj = eval(str);

      if (constructorObj && constructorObj.prototype instanceof Error) {
        proto = constructorObj.prototype;
      }
    } catch (_error) {
      console.warn(`Constructor "${str}" out of bounds, falling back to Error`);
    }
  }

  return Object.create(proto, props);
}

function genName<const T, const D = undefined | void>(req: RequestSchema<T, D>) {
  return req.input.toString();
}

function noopFormat<T>(data: T) {
  return data;
}

export function useRequest<const T, const D = undefined | void, const Q = JSONValue>(
  reqo: RequestSchema<T, D> | string,
  opts?: UseRequestOpts<T, Q>,
) {
  const req: RequestSchema<T, D> = useMemo(() => {
    return typeof reqo === 'string' ? { input: reqo } : reqo;
  }, [reqo]);
  const name = req.name ? req.name : genName(req);
  const pageContext = usePageContext();

  // biome-ignore lint/correctness/useExhaustiveDependencies(pageContext): As this runs on SSR only, never changes
  // biome-ignore lint/correctness/useExhaustiveDependencies(pageContext.fetch): SSR
  // biome-ignore lint/correctness/useExhaustiveDependencies(pageContext.headers): SSR
  const devFetch = useCallback((url: string, init?: RequestInit<RequestInitCfProperties>) => {
    return ('fetch' in pageContext ? pageContext.fetch : fetch)(url, {
      // Cloudflare Workers don't support fetch.credentials
      credentials: IS_SERVER ? undefined : 'include',
      // Cloudflare Workers don't support fetch.mode
      mode: IS_SERVER ? undefined : 'cors',
      ...init,
      headers: {
        ...(('headers' in pageContext && pageContext.headers) || undefined),
        ...(!!init?.headers?.entries && typeof init?.headers?.entries === 'function'
          ? Object.fromEntries(init.headers.entries() as IterableIterator<[string, string]>)
          : undefined),
        ...(!!init?.headers?.entries && typeof init?.headers?.entries === 'object'
          ? init.headers
          : undefined),
        'Content-Type': 'application/json',
        host: new URL(url).hostname,
      },
    });
  }, []);

  // biome-ignore lint/correctness/useExhaustiveDependencies: everything here should be static
  const ctxRequests: typeof pageContext.requests = useMemo(() => {
    return ('requests' in pageContext && pageContext.requests) || {};
  }, ['requests' in pageContext && pageContext.requests]);

  const deepOpts = useDeepMemo(opts);
  const deepQuery = useDeepMemo(deepOpts?.query);

  const formatResult = deepOpts?.formatResult || noopFormat;

  const useDataData = useData();
  const authToken = useDataData?.authToken;
  const authTokenRef = useRefUpdating(authToken);

  const baseCacheKey = useAsync(
    [USEREQUESTID, req.id, req.name, req.input, deepOpts?.cache, deepQuery],
    async () => {
      const requestInit = {
        ...req.init,
      };
      const requestSchema = {
        id: req.id || (typeof req.input === 'string' ? req.input : req.input?.url),
        ...req,
        init: requestInit,
      };
      const requestOpts = {
        query: deepQuery,
        hideAlert: deepOpts?.hideAlert,
        skipRefresh: deepOpts?.skipRefresh,
        fetch: IS_SERVER ? ('fetch' in pageContext && pageContext.fetch) || devFetch : undefined, // SSR fetch includes headers from page request
        authToken: authTokenRef.current,
        cache: deepOpts?.cache,
      };

      return await requestCacheKey(requestSchema, requestOpts);
    },
  );

  const alreadyHaveData = !!ctxRequests[baseCacheKey];
  const runAutomatically = !opts?.manual;
  const onlyRenderOnClient = !!opts?.clientOnly;

  const count = useRef(0);

  const abortControllerRef = useRef<AbortController>();
  const reqRef = useRefUpdating(req);
  const optsRef = useRefUpdating(opts);

  // biome-ignore lint/correctness/useExhaustiveDependencies(devFetch): SSR
  const fetcher = useCallback(
    async <const RE extends boolean = false, const Raw extends boolean = false>(
      props?: D,
      {
        throwOnRun,
        rawResult,
        returnError,
        dontStoreError,
        query: runQuery,
        req: reqfo,
        skipAbort = false,
      }: UseRequestRunFnOpts<RE, Raw, Q> = {},
    ): UseRequestRunFnResult<T, RE, Raw> => {
      const reqf = (typeof reqfo === 'string' ? { input: reqfo } : reqfo) as RequestSchema<T, D>;

      try {
        const prevAbortController = abortControllerRef.current;

        if (!skipAbort) {
          if (prevAbortController?.signal && !prevAbortController.signal.aborted) {
            // Abort the last request if we are running another one
            // This should prevent race conditions of setting result value
            // Note: if you are using .run() to FETCH different data, you will want to skipAbort
            // skipAbort is also automatically set by run if you do dontAlterState:true
            prevAbortController.abort?.();
          }
        }

        const nextAbortController = new AbortController();

        if (!skipAbort) {
          abortControllerRef.current = nextAbortController;
        }

        const requestInit = {
          ...reqRef.current.init,
          signal: reqf?.init?.signal || reqRef.current.init?.signal || nextAbortController.signal,
          ...reqf?.init,
        };
        const requestSchema = {
          id:
            reqf?.id ||
            (typeof reqf?.input === 'string' ? reqf.input : reqf?.input?.url) ||
            req.id ||
            (typeof req.input === 'string' ? req.input : req.input?.url),
          ...reqRef.current,
          ...reqf,
          init: requestInit,
        };
        const requestOpts = {
          body: props,
          // noStringify: true,
          query:
            runQuery && optsRef.current?.query
              ? { ...optsRef.current?.query, ...runQuery }
              : runQuery || optsRef.current?.query,
          hideAlert: optsRef.current?.hideAlert,
          skipRefresh: optsRef.current?.skipRefresh,
          fetch: IS_SERVER ? ('fetch' in pageContext && pageContext.fetch) || devFetch : undefined, // SSR fetch includes headers from page request
          authToken: authTokenRef.current,
          cache: optsRef.current?.cache,
          // withCacheKey: true,
        };

        const cacheKey = await requestCacheKey(requestSchema, requestOpts);

        requestSchema.id = cacheKey; // similar requests but different pages, for example, should show separate alerts

        const response = await request(requestSchema, requestOpts);

        const json: T = await formatResult(response);

        if (IS_SERVER) {
          ctxRequests[cacheKey] = extractResult(
            optsRef.current?.storeRaw ? { ...json } : json,
            optsRef.current?.storeRaw,
          );

          if ('requests' in pageContext && pageContext.requests) {
            pageContext.requests = { ...pageContext.requests, ...ctxRequests };
          } else {
            pageContext.requests = ctxRequests;
          }
        }

        return extractResult(json, rawResult) as ExtractRaw<Raw, T>;
      } catch (err) {
        if (!dontStoreError && err instanceof Error && IS_SERVER) {
          ctxRequests[baseCacheKey] = errorToObject(err);

          if ('requests' in pageContext && pageContext.requests) {
            pageContext.requests = { ...pageContext.requests, ...ctxRequests };
          } else {
            pageContext.requests = ctxRequests;
          }
        }

        if (throwOnRun) {
          throw err;
        }

        return (returnError ? err : null) as RE extends true ? Error : null;
      }
    },
    [
      formatResult,
      deepOpts?.hideAlert,
      deepQuery,
      deepOpts?.skipRefresh,
      deepOpts?.storeRaw,
      deepOpts?.cache,
      name,
      pageContext,
      req,
      ctxRequests,
      baseCacheKey,
    ],
  );

  const isFirst = useIsFirstRender();
  const useAsyncResultRef = useRef(onlyRenderOnClient && !IS_SERVER ? false : true);
  const runRef = useRef<typeof fetcher>();

  const generateInitialState = useCallback(async () => {
    console.log('ASYNC PREPPING INIT', baseCacheKey);
    // onServerAsync Server side
    if (ctxRequests[baseCacheKey]) {
      // Hydrate the client using the data from the server that was already fetched and pushed into context cache
      const result: T = await formatResult(ctxRequests[baseCacheKey]);
      const err =
        ctxRequests[baseCacheKey] instanceof Error
          ? (ctxRequests[baseCacheKey] as Error)
          : undefined;

      const isLoggedOut = err instanceof ExpiredSessionError || err instanceof NoSessionError;

      if (isLoggedOut && !IS_SERVER) {
        forceLogoutEventTarget.dispatchEvent(new CustomEvent('ras:forceLogout'));
      }

      if (!IS_SERVER) {
        // This is no longer needed in the cache; we've hydrated the client
        // delete ctxRequests[baseCacheKey];
      }

      useAsyncResultRef.current = true;

      return Object.create(Object.prototype, {
        data: { value: err ? null : result, enumerable: true },
        error: { value: err, enumerable: true },
        loading: { value: false, enumerable: true },
        pending: { value: false, enumerable: true },
        // LoginForm: { value: isLoggedOut ? LoginForm : undefined }, // not enumerable to avoid SSR serialization issue
        abort: { value: abortControllerRef.current }, // not enumerable to avoid SSR serialization issue
      }) as NonNullable<UseRequestResult<T, D, typeof err>>;
    } else if (runAutomatically && (!onlyRenderOnClient || !IS_SERVER)) {
      // Fetch this data for the first time (or because something changed)
      count.current++;

      const result = await fetcher(undefined, {
        returnError: true,
        rawResult: optsRef.current?.storeRaw || false,
      });
      const err = result instanceof Error ? result : undefined;

      const isLoggedOut = err instanceof ExpiredSessionError || err instanceof NoSessionError;

      const x = Object.create(Object.prototype, {
        data: { value: err ? null : (result as T), enumerable: true },
        error: { value: err, enumerable: true },
        loading: { value: false, enumerable: true },
        pending: { value: false, enumerable: true },
        // LoginForm: { value: isLoggedOut ? LoginForm : undefined }, // not enumerable to avoid SSR serialization issue
        abort: { value: abortControllerRef.current }, // not enumerable to avoid SSR serialization issue
      }) as NonNullable<UseRequestResult<T, D, typeof err>>;

      const { data: _, ...f } = x;

      useAsyncResultRef.current = true;

      return x;
    } else if (IS_SERVER && onlyRenderOnClient) {
      // console.log('not running on server', name);
      if (runAutomatically) {
        // special use case for useInfinite... we want to know if this is "pending" at hydration time
        return Object.create(Object.prototype, {
          loading: { value: false, enumerable: true },
          pending: { value: true, enumerable: true },
        }) as NonNullable<UseRequestResult<T, D, Error | undefined>>;
      }
    } else if (!runAutomatically) {
      // console.log('not running automatically', name);
    } else {
      // console.log('wtf?', name);
    }
    console.log('ASYNC RETURNING ELSEWHERE', baseCacheKey);
    return Object.create(Object.prototype, {}) as NonNullable<
      UseRequestResult<T, D, Error | undefined>
    >;
  }, []);

  const asyncResultServer = useAsync(
    deepOpts?.id
      ? [USEREQUESTID, deepOpts.id]
      : [USEREQUESTID, req.id, req.name, req.input, req.init?.body, deepQuery],
    generateInitialState,
  );

  const isServerResultValid = !!asyncResultServer && typeof asyncResultServer === 'object';

  // const asyncResultClient = useAsync(
  //   IS_SERVER
  //     ? '!ssrskip!'
  //     : deepOpts?.id
  //       ? [USEREQUESTID, deepOpts.id, 'csr']
  //       : [USEREQUESTID, req.id, req.name, req.input, req.init?.body, deepQuery, 'csr'],
  //   async () => {
  //     console.log('we made it to asyncResultClient', baseCacheKey, IS_SERVER);
  //     if (!IS_SERVER) {
  //       // onClientSync Client side
  //       if (runAutomatically && onlyRenderOnClient && isServerResultValid) {
  //         return await runRef.current?.();
  //       }

  //       const x = await generateInitialState();
  //       console.log('ASYNC BACKUP GEN!', baseCacheKey, x);
  //       return x;
  //     }

  //     return null;
  //   },
  // );
  const asyncResultClient = undefined;

  const isClientResultValid = !!asyncResultClient && typeof asyncResultClient === 'object';

  const asyncResult = isClientResultValid
    ? asyncResultClient
    : isServerResultValid
      ? asyncResultServer
      : null;

  if (!IS_SERVER && ctxRequests[baseCacheKey] && ctxRequests[baseCacheKey].__constructor__) {
    // Convert this error back once
    // @todo does this need to be moved somewhere else?!
    ctxRequests[baseCacheKey] = errorFromObject(ctxRequests[baseCacheKey]);
  }

  const [reducer, dispatch] = useReducer(requestReducer<T>, undefined, () => ({
    loading: // @todo prefill from asyncResult
      asyncResult?.loading !== undefined
        ? asyncResult?.loading
        : !alreadyHaveData && runAutomatically && (onlyRenderOnClient || !IS_SERVER)
          ? true
          : false,
    pending: // @todo prefill from asyncResult
      asyncResult?.pending !== undefined
        ? asyncResult?.pending
        : !alreadyHaveData && runAutomatically && (onlyRenderOnClient || !IS_SERVER)
          ? true
          : false,
    error: // @todo prefill from asyncResult
      asyncResult?.error !== undefined
        ? asyncResult?.error
        : ctxRequests[baseCacheKey] && ctxRequests[baseCacheKey] instanceof Error
          ? ctxRequests[baseCacheKey]
          : undefined,
    data: // @todo prefill from asyncResult
      asyncResult?.data !== undefined
        ? asyncResult?.data
        : ctxRequests[baseCacheKey] && !(ctxRequests[baseCacheKey] instanceof Error)
          ? ctxRequests[baseCacheKey]
          : undefined,
  }));
  const { loading, pending, error, data } = reducer;

  const [abortController, setAbortController] = useState<AbortController | undefined>(
    // @todo prefill from asyncResult
    asyncResult?.abort !== undefined ? asyncResult?.abort : abortControllerRef.current,
  );

  const run = useCallback(
    async <const RE extends boolean = false, const Raw extends boolean = false, const Query = Q>(
      props?: D,
      runOpts: UseRequestRunFnOpts<RE, Raw, Query> = {},
    ): UseRequestRunFnResult<T, RE, Raw> => {
      useAsyncResultRef.current = false;

      if (!runOpts?.dontAlterState) {
        dispatch({ action: 'SET', values: { loading: true, pending: true, error: null } });
      }

      try {
        const json = (await fetcher(props, {
          ...runOpts,
          throwOnRun: true,
          rawResult: true,
          // skipAbort=undefined + dontAlterState = skipAbort; otherwise, use skipAbort
          skipAbort:
            runOpts?.skipAbort === undefined ? runOpts?.dontAlterState : runOpts?.skipAbort,
        })) as T;

        useAsyncResultRef.current = false;

        if (!runOpts?.dontAlterState) {
          if (json !== null && json !== undefined) {
            dispatch({
              action: 'SET',
              values: {
                data: extractResult(json, optsRef.current?.storeRaw),
                error: null,
                loading: false,
                pending: false,
              },
            });
          } else {
            dispatch({
              action: 'SET',
              values: {
                data: undefined,
                error: null,
                loading: false,
                pending: false,
              },
            });
          }
        }

        return extractResult(json, runOpts.rawResult) as ExtractRaw<Raw, T>;
      } catch (err) {
        useAsyncResultRef.current = false;

        if (!runOpts?.dontAlterState) {
          if (!runOpts?.dontStoreError && err instanceof Error) {
            dispatch({
              action: 'SET',
              values: {
                error: err,
                loading: false,
                pending: false,
              },
            });
          } else {
            dispatch({
              action: 'SET',
              values: {
                loading: false,
                pending: false,
              },
            });
          }
        }

        if (runOpts?.throwOnRun) {
          throw err;
        }
      }

      return null;
    },
    [
      fetcher,
      formatResult,
      deepOpts?.hideAlert,
      deepQuery,
      deepOpts?.skipRefresh,
      deepOpts?.storeRaw,
      deepOpts?.cache,
      name,
      pageContext,
      req,
      ctxRequests,
      baseCacheKey,
    ],
  );

  const reset = useCallback(() => {
    useAsyncResultRef.current = false;

    dispatch({
      action: 'SET',
      values: {
        data: undefined,
        error: null,
        loading: false,
        pending: runAutomatically,
      },
    });
  }, []);

  runRef.current = run;

  const { data: __, ...e } = asyncResult || {};
  useEffectOnce(() => {
    // console.log(
    //   'we made it to client in useRequest',
    //   baseCacheKey,
    //   [asyncResult, isClientResultValid, asyncResultClient, isServerResultValid, asyncResultServer],
    //   deepOpts?.id
    //     ? [USEREQUESTID, deepOpts.id]
    //     : [USEREQUESTID, req.id, req.name, req.input, req.init?.body, deepQuery].join(''),
    // );
  });

  if (asyncResult) {
    if (!asyncResult?.dispatch) {
      // console.log(
      //   'about to defineProperty',
      //   baseCacheKey,
      //   [asyncResult],
      //   deepOpts?.id
      //     ? [USEREQUESTID, deepOpts.id]
      //     : [USEREQUESTID, req.id, req.name, req.input, req.init?.body, deepQuery].join(''),
      // );
      Object.defineProperty(asyncResult, 'dispatch', {
        value: dispatch,
        enumerable: false,
        configurable: false,
        writable: false,
      }); // not enumerable to avoid SSR serialization issue
    }

    if (!asyncResult?.run) {
      Object.defineProperty(asyncResult, 'run', {
        value: run,
        enumerable: false,
        configurable: false,
        writable: false,
      }); // not enumerable to avoid SSR serialization issue
    }
  }

  useEffect(() => {
    if (
      (!asyncResult || !('data' in asyncResult)) &&
      !IS_SERVER &&
      isFirst &&
      pending &&
      error === undefined &&
      data === undefined
    ) {
      // this is a first pass on client
      // console.log(
      //   'FIRST PASS',
      //   baseCacheKey,
      //   asyncResult,
      //   IS_SERVER,
      //   useAsyncResultRef.current,
      //   pending,
      //   error,
      //   data,
      // );
      run();
    } else {
      // console.log(
      //   'here is the first skipped run',
      //   baseCacheKey,
      //   asyncResult,
      //   IS_SERVER,
      //   useAsyncResultRef.current,
      //   pending,
      //   error,
      //   data,
      // );
    }
  }, [asyncResult, run]);

  // Prevent re-fetching too quickly
  const [deepQueryDebounced] = useDebounce(deepQuery, 150);

  useUpdateEffect(() => {
    if (!optsRef.current?.manual) {
      console.log('forcing run on client', baseCacheKey);
      runRef.current?.();
    }
  }, [deepQueryDebounced]);

  const memoizedResult = useMemo(
    () =>
      ({
        data: useAsyncResultRef.current ? asyncResult?.data || data : data,
        error: useAsyncResultRef.current ? asyncResult?.error || error : error,
        loading: useAsyncResultRef.current ? asyncResult?.loading || loading : loading,
        pending: useAsyncResultRef.current ? asyncResult?.pending || pending : pending,
        // LoginForm: error instanceof ExpiredSessionError ? LoginForm : undefined,
        dispatch,
        run,
        reset,
        abort: abortController,
      }) satisfies UseRequestResult<T, D, typeof error>,
    [
      abortController,
      asyncResult?.data,
      asyncResult?.error,
      asyncResult?.loading,
      asyncResult?.pending,
      data,
      error,
      loading,
      pending,
      run,
      reset,
    ],
  );

  const finalResult = useDeepMemo(IS_SERVER ? undefined : memoizedResult);

  if (IS_SERVER && asyncResult) {
    return asyncResult;
  }

  if (finalResult) {
    return finalResult;
  }

  return memoizedResult;
}

// For now this is mostly just duplicated from useRequest for the purpose of useReactInfiniteRequest
export function useRequestFetcher<const T, const D = undefined | void, const Q = JSONValue>(
  reqo: RequestSchema<T, D> | string,
  opts?: UseRequestOpts<T, Q>,
  dispatch?: any,
) {
  const req: RequestSchema<T, D> = useMemo(() => {
    return typeof reqo === 'string' ? { input: reqo } : reqo;
  }, [reqo]);
  const name = req.name ? req.name : genName(req);
  const pageContext = usePageContext();

  // biome-ignore lint/correctness/useExhaustiveDependencies(pageContext): As this runs on SSR only, never changes
  // biome-ignore lint/correctness/useExhaustiveDependencies(pageContext.fetch): SSR
  // biome-ignore lint/correctness/useExhaustiveDependencies(pageContext.headers): SSR
  const devFetch = useCallback((url: string, init?: RequestInit<RequestInitCfProperties>) => {
    return ('fetch' in pageContext ? pageContext.fetch : fetch)(url, {
      // Cloudflare Workers don't support fetch.credentials
      credentials: IS_SERVER ? undefined : 'include',
      // Cloudflare Workers don't support fetch.mode
      mode: IS_SERVER ? undefined : 'cors',
      ...init,
      headers: {
        ...(('headers' in pageContext && pageContext.headers) || undefined),
        ...(!!init?.headers?.entries && typeof init?.headers?.entries === 'function'
          ? Object.fromEntries(init.headers.entries() as IterableIterator<[string, string]>)
          : undefined),
        ...(!!init?.headers?.entries && typeof init?.headers?.entries === 'object'
          ? init.headers
          : undefined),
        'Content-Type': 'application/json',
        host: new URL(url).hostname,
      },
    });
  }, []);

  const deepOpts = useDeepMemo(opts);
  const deepQuery = useDeepMemo(deepOpts?.query);

  const formatResult = deepOpts?.formatResult || noopFormat;

  const useDataData = useData();
  const authToken = useDataData?.authToken;
  const authTokenRef = useRefUpdating(authToken);

  // console.log('baseCacheKey', name, [
  //   USEREQUESTID,
  //   req.id,
  //   req.name,
  //   req.input,
  //   deepOpts?.cache,
  //   deepQuery,
  // ]);
  const baseCacheKey = useAsync(
    [USEREQUESTID, req.id, req.name, req.input, deepOpts?.cache, deepQuery],
    async () => {
      const requestInit = {
        ...req.init,
      };
      const requestSchema = {
        id: req.id || (typeof req.input === 'string' ? req.input : req.input?.url),
        ...req,
        init: requestInit,
      };
      const requestOpts = {
        query: deepQuery,
        hideAlert: deepOpts?.hideAlert,
        skipRefresh: deepOpts?.skipRefresh,
        fetch: IS_SERVER ? ('fetch' in pageContext && pageContext.fetch) || devFetch : undefined, // SSR fetch includes headers from page request
        authToken: authTokenRef.current,
        cache: deepOpts?.cache,
      };

      return await requestCacheKey(requestSchema, requestOpts);
    },
  );

  const abortControllerRef = useRef<AbortController>();
  const reqRef = useRefUpdating(req);
  const optsRef = useRefUpdating(opts);

  // biome-ignore lint/correctness/useExhaustiveDependencies(devFetch): SSR
  const fetcher = useCallback(
    async <const RE extends boolean = false, const Raw extends boolean = false, const Query = Q>(
      props?: D,
      {
        throwOnRun,
        rawResult,
        returnError,
        query: runQuery,
        req: reqfo,
        skipAbort = false,
        abortController: runAbortController,
      }: UseRequestRunFnOpts<RE, Raw, Query> = {},
    ): UseRequestRunFnResult<T, RE, Raw> => {
      const reqf = (typeof reqfo === 'string' ? { input: reqfo } : reqfo) as RequestSchema<T, D>;

      try {
        const prevAbortController = abortControllerRef.current;

        if (!skipAbort) {
          if (prevAbortController?.signal && !prevAbortController.signal.aborted) {
            prevAbortController.abort?.();
          }
        }

        const nextAbortController = runAbortController || new AbortController();

        if (!skipAbort) {
          abortControllerRef.current = nextAbortController;
        }

        const requestInit = {
          ...reqRef.current?.init,
          signal: reqf?.init?.signal || reqRef.current?.init?.signal || nextAbortController.signal,
          ...reqf?.init,
        };
        const requestSchema = {
          id:
            reqf?.id ||
            (typeof reqf?.input === 'string' ? reqf.input : reqf?.input?.url) ||
            req.id ||
            (typeof req.input === 'string' ? req.input : req.input?.url),
          ...reqRef.current,
          ...reqf,
          init: requestInit,
        };
        const requestOpts = {
          body: props,
          // noStringify: true,
          query:
            runQuery && optsRef.current?.query
              ? { ...optsRef.current?.query, ...runQuery }
              : runQuery || optsRef.current?.query,
          hideAlert: optsRef.current?.hideAlert,
          skipRefresh: optsRef.current?.skipRefresh,
          fetch: IS_SERVER ? ('fetch' in pageContext && pageContext.fetch) || devFetch : undefined, // SSR fetch includes headers from page request
          authToken: authTokenRef.current,
          cache: optsRef.current?.cache,
          // withCacheKey: true,
        };

        const cacheKey = await requestCacheKey(requestSchema, requestOpts);

        requestSchema.id = cacheKey; // similar requests but different pages, for example, should show separate alerts

        const response = await request(requestSchema, requestOpts);

        const json: T = await formatResult(response);

        return extractResult(json, rawResult) as ExtractRaw<Raw, T>;
      } catch (err) {
        if (throwOnRun) {
          throw err;
        }

        return (returnError ? err : null) as RE extends true ? Error : null;
      }
    },
    [
      formatResult,
      deepOpts?.hideAlert,
      deepQuery,
      deepOpts?.skipRefresh,
      deepOpts?.storeRaw,
      deepOpts?.cache,
      name,
      pageContext,
      req,
      baseCacheKey,
    ],
  );

  const [abortController, setAbortController] = useState<AbortController | undefined>(
    abortControllerRef.current,
  );

  const run = useCallback(
    async <const RE extends boolean = false, const Raw extends boolean = false>(
      props?: D,
      runOpts: UseRequestRunFnOpts<RE, Raw, Q> = {},
    ): UseRequestRunFnResult<T, RE, Raw> => {
      if (!runOpts?.dontAlterState) {
        dispatch?.({ action: 'SET', values: { loading: true, pending: true, error: null } });
      }

      try {
        const json = (await fetcher(props, { ...runOpts, throwOnRun: true, rawResult: true })) as T;

        if (!runOpts?.dontAlterState) {
          if (json !== null && json !== undefined) {
            dispatch?.({
              action: 'SET',
              values: {
                data: extractResult(json, optsRef.current?.storeRaw),
                error: null,
                loading: false,
                pending: false,
              },
            });
          } else {
            dispatch?.({
              action: 'SET',
              values: {
                data: undefined,
                error: null,
                loading: false,
                pending: false,
              },
            });
          }
        }

        return extractResult(json, runOpts.rawResult) as ExtractRaw<Raw, T>;
      } catch (err) {
        if (!runOpts?.dontAlterState) {
          if (!runOpts?.dontStoreError && err instanceof Error) {
            dispatch?.({
              action: 'SET',
              values: {
                error: err,
                loading: false,
                pending: false,
              },
            });
          } else {
            dispatch?.({
              action: 'SET',
              values: {
                loading: false,
                pending: false,
              },
            });
          }
        }

        if (runOpts?.throwOnRun) {
          throw err;
        }
        console.log('we had an error', err);
      }

      return null;
    },
    [
      fetcher,
      formatResult,
      deepOpts?.hideAlert,
      deepQuery,
      deepOpts?.skipRefresh,
      deepOpts?.storeRaw,
      deepOpts?.cache,
      name,
      pageContext,
      req,
      baseCacheKey,
    ],
  );

  const memoizedResult = useMemo(
    () =>
      ({
        dispatch,
        run,
        abort: abortController,
      }) satisfies UseRequestResult<T, D, Error | undefined>,
    [abortController, run],
  );

  // const finalResult = useDeepMemo(IS_SERVER ? undefined : memoizedResult);

  // if (finalResult) {
  //   return finalResult;
  // }

  return memoizedResult;
}
